import React, { useCallback, useState, useEffect } from "react";
import {
  ReactFlow,
  Background,
  MiniMap,
  Controls,
  useNodesState,
  useEdgesState,
  Handle,
  getBezierPath,
  EdgeLabelRenderer,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import Secondary from "../../../components/Button/Secondary";
import dagre from "dagre";
import { FaDatabase } from "react-icons/fa";
import { AiOutlineCluster } from "react-icons/ai";
import styled from "styled-components/macro";
import {
  MdCancel,
  MdCached,
  MdCheckCircle,
  MdHourglassEmpty,
  MdFeed,
  MdHourglassFull,
} from "react-icons/md";
import { useApi } from "../../../api/useApi";
import ErrorMessages from "../../../components/Notifications/ErrorMessages";
import { formatMinutes } from "../../../common/formats";
import { startDataSourceIngress } from "../../../api/dataSourceMutations";
import { startServicerTransferFeed } from "../../../api/serviceTransferMutations";
import { runEtlPipeline } from "../../../api/etlProviderMutations";
import useSetWorkflowPipelineCompleteEvent from "../../../Hooks/useSetWorkflowPipelineCompleteEvent";
import useSetWorkflowPipelineFailEvent from "../../../Hooks/useSetWorkflowPipelineFailEvent";
import { toast } from "react-toastify";
import Spinner from "../../../components/Loaders/Spinner";
import StyledLink from "../../../components/StyledLink";
import { dayOptions } from "../../../components/Forms/ETL/CreateFlow.js";

const Indicators = styled.div`
  align-items: center;
  display: flex;
  font-size: 0.875rem;
`;

const resultStateEnumsSource = [
  { value: "NONE", label: "Report Running" },
  { value: "REPORT_READY", label: "Completed" },
  { value: "REFERENCE_REPORT_READY", label: "Completed (Reference)" },
  { value: "REPORT_READY_INCOMPLETE", label: "Incomplete" },
  { value: "TIMED_OUT", label: "Timed Out" },
  { value: "CONNECTION_FAILURE", label: "Connection Failure" },
  { value: "CANCELLED", label: "Cancelled" },
  { value: "INVALID_RULE_MAPPING", label: "Invalid Rule Mapping" },
  { value: "INGRESS_SOURCE_NOT_FOUND", label: "Waiting for New File" },
  { value: "GENERIC_FAILURE", label: "Failed" },
];

function showStateIconSource(dataScore, thresholdScore, resultState) {
  switch (resultState) {
    case "NONE":
    case "CANCELLED":
    case "INVALID_RULE_MAPPING":
    case "INGRESS_SOURCE_NOT_FOUND":
    case "GENERIC_FAILURE":
    case "CONNECTION_FAILURE":
    case "TIMED_OUT":
    case "REPORT_READY_INCOMPLETE":
      return (
        <div style={{ display: "flex" }}>
          <Indicators
            style={{
              color: "#fff",
              background: "#f87e7e",
              padding: ".2rem",
              borderRadius: "4px",
              paddingLeft: ".4rem",
              paddingRight: ".4rem",
              fontSize: ".875rem",
              maxWidth: "200px",
            }}
            title="Failure"
          >
            <div
              style={{
                marginRight: ".2rem",
                display: "flex",
                alignItems: "center",
              }}
            >
              <MdCancel />
            </div>
            {resultStateEnumsSource.find(
              (resEnum) => resEnum.value === resultState
            )?.label ?? "No Run Available"}
          </Indicators>
          {dataScore !== null ? (
            <Indicators
              style={{
                marginLeft: ".5rem",
                color: thresholdScore > dataScore ? "#333" : "#274B86",
                background: thresholdScore > dataScore ? "#f87e7e" : "#d2dcee",
                padding: "0.2rem 0.4rem",
                borderRadius: "4px",
                border: `1px solid ${
                  thresholdScore > dataScore ? "#333" : "#274B86"
                }`,
                fontSize: "0.875rem",
              }}
              title={"score"}
            >
              Score: {dataScore}
            </Indicators>
          ) : null}
        </div>
      );

    case "REPORT_READY":
      return (
        <div style={{ display: "flex" }}>
          <Indicators
            style={{
              color: "#349004",
              background: "#dfedda none repeat scroll 0% 0%",
              padding: " 0.2rem 0.4rem",
              borderRadius: "4px",
              border: "1px solid #349004",
              fontSize: ".875rem",
            }}
            title="Success"
          >
            <div
              style={{
                marginRight: ".2rem",
                display: "flex",
                alignItems: "center",
              }}
            >
              <MdCheckCircle />
            </div>

            {resultStateEnumsSource.find(
              (resEnum) => resEnum.value === resultState
            )?.label ?? null}
          </Indicators>

          {dataScore !== null ? (
            <Indicators
              style={{
                marginLeft: ".5rem",
                color: thresholdScore > dataScore ? "#333" : "#274B86",
                background: thresholdScore > dataScore ? "#f87e7e" : "#d2dcee",
                padding: "0.2rem 0.4rem",
                borderRadius: "4px",
                border: `1px solid ${
                  thresholdScore > dataScore ? "#333" : "#274B86"
                }`,
                fontSize: "0.875rem",
              }}
              title={"score"}
            >
              Score: {dataScore}
            </Indicators>
          ) : null}
        </div>
      );
    default:
      return null;
  }
}

// TODO ADD THRESHOLD CHECK
const resultStateEnums = [
  { value: null, label: "Waiting on Dependencies" },
  { value: "NONE", label: "Waiting on Dependencies" },
  { value: "PIPELINE_QUEUED", label: "Queued" },
  { value: "PIPELINE_QUEUED_AND_ALERTED", label: "Queued And Alerted" },
  { value: "PIPELINE_STARTED", label: "Started" },
  { value: "PIPELINE_SUCCEEDED", label: "Success" },
  { value: "CONNECTION_FAILURE", label: "Connection Failure" },
  { value: "CANCELLED", label: "Cancelled" },
  { value: "PIPELINE_FAILED", label: "Failed" },
  { value: "GENERIC_FAILURE", label: "Failure" },
  {
    value: "INGRESS_SOURCE_NOT_FOUND",
    label:
      "The platform could not find the ingress source; it may have been moved or deleted",
  },
  {
    value: "INVALID_RULE_MAPPING",
    label:
      "A report could not be generated due to one or more invalid rule mappings",
  },
  {
    value: "REFERENCE_REPORT_QUEUED",
    label:
      "A reference report has been queued, starting a data source run without running rules",
  },
  {
    value: "REFERENCE_REPORT_READY",
    label:
      "A reference report has run successfully, creating a reference target but without running rules",
  },
  { value: "REPORT_READY", label: "The refresh has generated a report" },
  {
    value: "REPORT_READY_INCOMPLETE",
    label:
      "The refresh has generated an incomplete report due to not all rows being measured",
  },
  {
    value: "TIMED_OUT",
    label:
      "The summary took twice as long as the previous summary and was automatically cancelled",
  },
  { value: "USER_CANCELLED", label: "User Cancelled" },
];

function showStateIcon(resultState, segment) {
  switch (resultState) {
    case null:
    case "NONE":
      return (
        <Indicators title="In Progress">
          <div
            style={{
              marginRight: ".2rem",
              display: "flex",
              alignItems: "center",
            }}
          >
            <MdHourglassEmpty />
          </div>
          {
            resultStateEnums.find((resEnum) => resEnum.value === resultState)
              ?.label
          }
        </Indicators>
      );
    case "CONNECTION_FAILURE":
    case "CANCELLED":
    case "PIPELINE_FAILED":
    case "GENERIC_FAILURE":
    case "INGRESS_SOURCE_NOT_FOUND":
    case "INVALID_RULE_MAPPING":
    case "TIMED_OUT":
    case "REPORT_READY_INCOMPLETE":
    case "USER_CANCELLED":
      return (
        <Indicators
          style={{ color: "#f87e7e", maxWidth: "200px" }}
          title="Failure"
        >
          <div
            style={{
              marginRight: ".2rem",
              display: "flex",
              alignItems: "center",
            }}
          >
            <MdCancel />
          </div>
          {
            resultStateEnums.find((resEnum) => resEnum.value === resultState)
              ?.label
          }
        </Indicators>
      );
    case "PIPELINE_QUEUED":
    case "PIPELINE_QUEUED_AND_STARTED":
    case "PIPELINE_STARTED":
    case "REFERENCE_REPORT_QUEUED":
      return (
        <Indicators style={{ color: "#009fd4" }} title="In Progress">
          <div
            style={{
              marginRight: ".2rem",
              display: "flex",
              alignItems: "center",
            }}
          >
            <MdCached />
          </div>

          {
            resultStateEnums.find((resEnum) => resEnum.value === resultState)
              ?.label
          }
        </Indicators>
      );
    case "PIPELINE_SUCCEEDED":
    case "REPORT_READY":
    case "REFERENCE_REPORT_READY":
      if (
        segment?.type === "DATA_SOURCE" &&
        segment?.dataSourceDataScore < segment?.dataSourceDataQualityThreshold
      ) {
        return (
          <Indicators
            style={{ color: "#f87e7e", maxWidth: "200px" }}
            title="Failure"
          >
            <div
              style={{
                marginRight: ".2rem",
                display: "flex",
                alignItems: "center",
              }}
            >
              <MdCancel />
            </div>
            {
              resultStateEnums.find((resEnum) => resEnum.value === resultState)
                ?.label
            }{" "}
            Threshold Not Met
          </Indicators>
        );
      } else {
        return (
          <Indicators style={{ color: "#349004" }} title="Success">
            <div
              style={{
                marginRight: ".2rem",
                display: "flex",
                alignItems: "center",
              }}
            >
              <MdCheckCircle />
            </div>

            {
              resultStateEnums.find((resEnum) => resEnum.value === resultState)
                ?.label
            }
          </Indicators>
        );
      }
      break;
    default:
      return null;
  }
}

function showStateIconIndicator(resultState, segment) {
  switch (resultState) {
    case "CONNECTION_FAILURE":
    case "CANCELLED":
    case "PIPELINE_FAILED":
    case "GENERIC_FAILURE":
      return (
        <Indicators
          style={{ color: "#f87e7e", maxWidth: "200px" }}
          title="Failure"
        >
          <MdCancel />
        </Indicators>
      );
    case "PIPELINE_QUEUED":
    case "PIPELINE_QUEUED_AND_STARTED":
    case "PIPELINE_STARTED":
      return (
        <Indicators style={{ color: "#009fd4" }} title="In Progress">
          <MdCached />
        </Indicators>
      );
    case "PIPELINE_SUCCEEDED":
    case "REPORT_READY":
    case "REFERENCE_REPORT_READY":
      if (
        segment?.type === "DATA_SOURCE" &&
        segment?.dataSourceDataScore < segment?.dataSourceDataQualityThreshold
      ) {
        return (
          <Indicators style={{ color: "#f87e7e" }} title="Failure">
            <MdCancel />
          </Indicators>
        );
      } else {
        return (
          <Indicators style={{ color: "green" }} title="Success">
            <MdCheckCircle />
          </Indicators>
        );
      }
      break;
    default:
      return (
        <Indicators style={{ color: "#ccc" }} title="Waiting for action">
          <MdHourglassFull />
        </Indicators>
      );
  }
}

const CustomEdge = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  data,
  markerEnd,
}) => {
  // Calculate the edge path and label position
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  return (
    <>
      <path
        id={id}
        style={style}
        className="react-flow__edge-path"
        d={edgePath}
        markerEnd={markerEnd}
      />
      <EdgeLabelRenderer>
        <div
          style={{
            position: "absolute",
            transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
            pointerEvents: "all",
          }}
          className="nodrag nopan"
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "#fff",
              borderRadius: "4px",
              padding: "2px 4px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
            }}
          >
            <span>
              {showStateIconIndicator(
                data?.sourceType === "DATA_SOURCE"
                  ? data?.dataSourceRefreshSummaryResultState
                  : data?.sourceType === "ETL_PIPELINE"
                  ? data?.etlPipelineResultState
                  : data?.sourceType === "TRANSFORMATION"
                  ? data?.servicerTransferSummaryResultState
                  : null,
                data
              )}

              {data.label}
            </span>
          </div>
        </div>
      </EdgeLabelRenderer>
    </>
  );
};

const edgeTypes = {
  custom: CustomEdge,
};

const IconSet = (entityType) => {
  switch (entityType) {
    case "DATA_SOURCE":
      return <FaDatabase />;
    case "ETL_PIPELINE":
      return <AiOutlineCluster />;
    case "TRANSFORMATION":
      return <MdFeed />;
    default:
      return null;
  }
};

// Define CustomNode first
const CustomNode = ({ id, data }) => {
  const [sentETL, setSentETL] = useState(false);
  const [etlErrors, setEtlErrors] = useState(null);

  const [{ errors: runEtlErrors }, runEtl] = useApi();
  const [
    {
      errors: startIngressErrors,
      loading: startLoadingIngressData,
      data: startIngressData,
    },
    startIngressProcess,
  ] = useApi();

  const startIngress = useCallback(
    (id) => {
      startIngressProcess({
        query: startDataSourceIngress,
        variables: { dataSourceId: id },
      });
    },
    [startIngressProcess]
  );

  const [
    { errors: startFeedErrors, data: startFeedExport },
    doStartFeedExport,
  ] = useApi();

  const createCSV = useCallback(
    ({ feedId }) => {
      doStartFeedExport({
        query: startServicerTransferFeed,
        variables: {
          feedId: feedId,
        },
      });
    },
    [doStartFeedExport]
  );

  const { workflowPipelineCompleteEvent } =
    useSetWorkflowPipelineCompleteEvent();

  const { workflowPipelineFailEvent } = useSetWorkflowPipelineFailEvent();

  //updated event
  useEffect(() => {
    if (workflowPipelineFailEvent) {
      if (workflowPipelineFailEvent?.payload?.EtlPipelineName === data?.name) {
        setEtlErrors([
          {
            message: workflowPipelineFailEvent?.payload?.ErrorMessage,
          },
        ]);

        setSentETL(false);
      }
    }
  }, [workflowPipelineFailEvent, data]);

  useEffect(() => {
    if (runEtlErrors?.length) {
      setSentETL(false);
    }
  }, [runEtlErrors]);

  //updated event
  useEffect(() => {
    if (workflowPipelineCompleteEvent) {
      if (
        workflowPipelineCompleteEvent?.payload?.EtlPipelineName === data?.name
      ) {
        setSentETL(false);
      }
    }
  }, [workflowPipelineCompleteEvent, data]);

  useEffect(() => {
    if (startFeedExport && !startFeedErrors) {
      toast.success("Transformation Started", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    }
  }, [startFeedExport, startFeedErrors]);

  useEffect(() => {
    if (startIngressData && !startIngressErrors) {
      toast.success("Source Started", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    }
  }, [startIngressData, startIngressErrors]);

  const { label, type, dataQualityThreshold, entityName } = data;

  return (
    <div
      style={{
        padding: 10,
        border: "1px solid #ddd",
        borderRadius: 5,
        backgroundColor: "#fff",
      }}
    >
      <div style={{ display: "flex", marginBottom: "1rem" }}>
        <div
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            marginRight: "1rem",
          }}
        >
          {showStateIcon(
            data?.type === "DATA_SOURCE"
              ? data?.dataSourceRefreshSummaryResultState
              : data?.type === "ETL_PIPELINE"
              ? data?.etlPipelineResultState
              : data?.type === "TRANSFORMATION"
              ? data?.servicerTransferSummaryResultState
              : null,
            data
          )}
        </div>
        <div
          style={{
            marginLeft: "auto",
          }}
        >
          {data?.type === "DATA_SOURCE" && (
            <Secondary
              title={"Run Report"}
              disabled={sentETL}
              onClick={() => {
                startIngress(Number(data?.entityId));
              }}
            >
              {startLoadingIngressData ? <Spinner /> : "Run Report"}
            </Secondary>
          )}
          {data?.type === "ETL_PIPELINE" && (
            <Secondary
              title={"Run ETL"}
              disabled={sentETL}
              onClick={() => {
                runEtl({
                  query: runEtlPipeline,
                  variables: {
                    pipelineName: data?.entityName,
                    etlProviderInstanceId: data?.etlProviderInstanceId,
                  },
                });
                setSentETL(true);
              }}
            >
              {sentETL ? <Spinner /> : "Run ETL"}
            </Secondary>
          )}
          {data?.type === "TRANSFORMATION" && (
            <Secondary
              title={"Run Transformation"}
              disabled={sentETL}
              onClick={() => {
                createCSV({
                  feedId: data?.entityId,
                });
              }}
            >
              {sentETL ? <Spinner /> : "Run Transformation"}
            </Secondary>
          )}
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "flex-start" }}>
        <div style={{ marginTop: "0.36rem", marginRight: "1rem" }}>
          <div
            style={{
              fontWeight: "bold",
              display: "flex",
              alignItems: "center",
            }}
          >
            <div style={{ marginRight: ".2rem" }}>{IconSet(label)}</div>
            {label}
          </div>
          <div
            style={{ maxWidth: "200px", fontSize: ".8rem", marginTop: ".2rem" }}
          >
            {entityName}
          </div>
        </div>
        <div
          style={{
            marginLeft: "auto",
            display: "flex",
            flexDirection: "column",
            alignContent: "right",
          }}
        >
          {type === "DATA_SOURCE" ? (
            <>
              <div style={{ display: "flex", flexDirection: "column" }}>
                <div>
                  <strong>Min. DQ Score: {dataQualityThreshold}</strong>
                </div>
              </div>

              <div style={{ display: "flex", alignItems: "center" }}>
                <div style={{ marginLeft: "auto" }}>
                  {showStateIconSource(
                    data.dataSourceDataScore,
                    dataQualityThreshold,
                    data.dataSourceRefreshSummaryResultState
                  )}
                </div>
              </div>
            </>
          ) : null}
        </div>
      </div>
      <Handle
        type="source" // Outgoing connections
        position="bottom"
        isConnectable="false"
      />
      <Handle
        type="target" // Incoming connections
        position="top"
        isConnectable="false"
      />

      {startFeedErrors && <ErrorMessages errors={startFeedErrors} />}
      {startIngressErrors && <ErrorMessages errors={startIngressErrors} />}
      {runEtlErrors && <ErrorMessages errors={runEtlErrors} />}
      {etlErrors && <ErrorMessages errors={etlErrors} />}
    </div>
  );
};

// Now define nodeTypes after CustomNode
const nodeTypes = { customNode: CustomNode };

const WorkflowBuilder = ({ workflowId, data: initialWorkflow }) => {
  const [nodes, setNodes] = useNodesState([]);
  const [edges, setEdges] = useEdgesState([]);

  useEffect(() => {
    if (initialWorkflow) loadWorkflow(initialWorkflow);
  }, [initialWorkflow]);

  const applyTreeLayout = (nodes, edges) => {
    const dagreGraph = new dagre.graphlib.Graph();
    dagreGraph.setDefaultEdgeLabel(() => ({}));
    dagreGraph.setGraph({ rankdir: "TB", align: "UL" }); // Top-to-Bottom layout

    // Add nodes to the Dagre graph
    nodes.forEach((node) => {
      dagreGraph.setNode(node.id, { width: 430, height: 230 }); // Adjust dimensions
    });

    // Add edges to the Dagre graph
    edges.forEach((edge) => {
      dagreGraph.setEdge(edge.source, edge.target);
    });

    // Perform Dagre layout
    dagre.layout(dagreGraph);

    // Update node positions based on Dagre's layout
    const updatedNodes = nodes.map((node) => {
      const dagreNode = dagreGraph.node(node.id);
      return {
        ...node,
        position: {
          x: dagreNode.x - 100, // Center adjustment
          y: dagreNode.y,
        },
        draggable: false, // Lock position for clean layout
      };
    });

    return updatedNodes;
  };

  // Apply this layout after loading workflow
  const loadWorkflow = (workflow) => {
    if (!workflow.dependencies) return;

    const dependencies = workflow.dependencies;

    // Step 1: Create nodes
    const nodes = dependencies.map((dependency) => ({
      id: `${dependency.type}-${dependency.id}`, // Combine type and id
      data: {
        label: dependency.type,
        type: dependency.type,
        entityName: dependency.friendlyName || dependency.name,
        entityId: dependency.id,
        dataQualityThreshold: dependency.dataSourceDataQualityThreshold,
        etlProviderInstanceId: dependency.etlProviderInstanceId,
        dataSourceRefreshSummaryResultState:
          dependency.dataSourceRefreshSummaryResultState,
        dataSourceDataScore: dependency.dataSourceDataScore,
        etlPipelineResultState: dependency.etlPipelineResultState,
        servicerTransferSummaryResultState:
          dependency.servicerTransferSummaryResultState,
      },
      position: { x: 0, y: 0 }, // Placeholder
      type: "customNode",
    }));

    const createEdges = (dependencyArray, targetId, type, dependencies) =>
      dependencyArray.map((dep) => {
        const foundDep = dependencies.find(
          (d) => d.id === dep.id && d.type === type
        );

        return {
          id: `e${type}-${dep.id}-${targetId}`, // Unique edge ID
          source: `${type}-${dep.id}`, // Match node IDs with type-prefix
          target: targetId, // Target node ID (already includes the type prefix)
          type: "custom", // Custom edge type
          animated: true,
          data: {
            label: "",
            source: `${type}-${dep.id}`,
            sourceName: foundDep?.friendlyName ?? foundDep?.name,
            sourceType: type,
            dataSourceRefreshSummaryResultState:
              foundDep.dataSourceRefreshSummaryResultState,
            dataSourceDataScore: foundDep.dataSourceDataScore,
            etlPipelineResultState: foundDep.etlPipelineResultState,
            servicerTransferSummaryResultState:
              foundDep.servicerTransferSummaryResultState,
          },
          markerEnd: { type: "arrowclosed", color: "black" }, // Arrow points to the target
        };
      });

    // Step 2: Create edges
    const edges = dependencies.flatMap((dependency) => {
      const targetId = `${dependency.type}-${dependency.id}`; // Match the node ID format

      return [
        ...createEdges(
          dependency.dataSourceDependencies,
          targetId,
          "DATA_SOURCE",
          dependencies
        ),
        ...createEdges(
          dependency.pipelineDependencies,
          targetId,
          "ETL_PIPELINE",
          dependencies
        ),
        ...createEdges(
          dependency.transformationDependencies,
          targetId,
          "TRANSFORMATION",
          dependencies
        ),
      ];
    });

    // Step 3: Apply layout
    const layoutNodes = applyTreeLayout(nodes, edges);

    // Update state
    setNodes(layoutNodes);
    setEdges(edges);
  };

  const proOptions = { hideAttribution: true };
  return (
    <>
      <div
        style={{
          height: "calc(100vh - 200px)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            flex: 1,
            marginBottom: "1rem",
            marginLeft: "1rem",
            marginRight: "1rem",
          }}
        >
          <>
            <div style={{ fontWeight: "bold", marginBottom: ".5rem" }}>
              Alert Time
            </div>
            <div style={{ display: "flex", alignItems: "center" }}>
              <div
                style={{
                  marginRight: "1rem",
                  display: "flex",
                }}
              >
                {{
                  HOURLY: "Hourly",
                  QUARTER_DAILY: "Quarter Daily",
                  DAILY: "Daily",
                  WEEKLY: `Weekly (${initialWorkflow.alertTriggerTime?.weekday})`,
                  MONTHLY: `Monthly (${
                    dayOptions.find(
                      (o) => o.value == initialWorkflow.alertTriggerTime?.day
                    )?.label
                  })`,
                }[initialWorkflow.alertTriggerTime?.frequency] || ""}
              </div>
              <div
                style={{
                  marginRight: "1rem",
                  display: "flex",
                }}
              >
                {formatMinutes(new Date(initialWorkflow.alertTriggerTimestamp))}
              </div>
              <div style={{ marginLeft: "auto" }}>
                <StyledLink
                  to={`/workflows/${workflowId}/edit`}
                  title="Edit Workflow"
                >
                  Edit
                </StyledLink>
              </div>
            </div>
          </>
        </div>

        <ReactFlow
          nodes={nodes.map((node) => ({
            ...node,
            data: {
              ...node.data,
            },
          }))}
          edges={edges}
          edgeTypes={edgeTypes} // Register custom edge type
          nodeTypes={nodeTypes}
          proOptions={proOptions}
          edgesUpdatable={false}
          edgesFocusable={false}
          nodesDraggable={false}
          nodesConnectable={false}
          fitView
        >
          <MiniMap />
          <Controls />
          <Background />
        </ReactFlow>
      </div>
    </>
  );
};

export default WorkflowBuilder;
